import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { NarratorGender } from '@storees/api-types';

export interface VoiceRecordingData {
  recordDataBase64?: string;
  /** Relative path under Directory.Data from capacitor-voice-recorder when using directory option. */
  recordFilesystemPath?: string;
  msDuration: number;
  mimeType: string;
  voiceName: string;
  voiceType: 'custom' | 'mom' | 'dad';
  avatarUrl?: string;
  voiceCloneId?: string;
  voiceCloneRequestId?: string;
  narratorGender?: NarratorGender;
  /** Named custom slot id (`voice-clone-custom-*`); omit for mom/dad. */
  customNarratorId?: string;
  /**
   * Set by `loadPersistedSlotRecording` only. Back uses this with `fromVoices` so we do not rely on
   * path strings that differ across web, iOS, and Android.
   */
  loadedFromPersistedSlot?: boolean;
}

/** Subdirectory (under Directory.Data) for session recordings from VoiceRecorder.startRecording. */
export const VOICE_CLONE_SESSION_SUBDIR = 'voice-clone-session';

const SLOTS_DIR = 'voice-clone/slots';
const REQUESTS_DIR = 'voice-clone/requests';

interface PersistedSlotMeta {
  msDuration: number;
  mimeType: string;
  audioRelPath: string;
}

@Injectable({
  providedIn: 'root',
})
export class VoiceRecordingService {
  private recordingData: VoiceRecordingData | null = null;

  setRecordingData(data: VoiceRecordingData): void {
    this.recordingData = data;
  }

  getRecordingData(): VoiceRecordingData | null {
    return this.recordingData;
  }

  /**
   * Clears the in-memory recording reference only (no filesystem deletes).
   * After Save, call this once `persistCurrentRecordingForSlot` has finished (session temp may
   * already have been removed inside persist). For abandoning a session capture, prefer
   * {@link clearAfterDiscard}.
   */
  clearRecordingData(): void {
    this.recordingData = null;
  }

  /**
   * Removes all local voice-clone recordings for the current device.
   * Used when anonymous voice data must not carry into a signed-in account.
   */
  async clearAllLocalVoiceCloneFiles(): Promise<void> {
    this.recordingData = null;

    await Filesystem.rmdir({
      directory: Directory.Data,
      path: SLOTS_DIR,
      recursive: true,
    }).catch(() => undefined);

    await Filesystem.rmdir({
      directory: Directory.Data,
      path: REQUESTS_DIR,
      recursive: true,
    }).catch(() => undefined);

    await Filesystem.rmdir({
      directory: Directory.Data,
      path: VOICE_CLONE_SESSION_SUBDIR,
      recursive: true,
    }).catch(() => undefined);
  }

  /**
   * Use when the user discards the current take.
   * Removes the session temp file under `voice-clone-session/` when present; never deletes
   * persisted slot audio under `voice-clone/slots/`.
   */
  async clearAfterDiscard(): Promise<void> {
    const data = this.recordingData;
    const path = data?.recordFilesystemPath?.trim();
    if (path && VoiceRecordingService.isSessionCapturePath(path)) {
      await Filesystem.deleteFile({
        directory: Directory.Data,
        path,
      }).catch(() => undefined);
    }
    this.recordingData = null;
  }

  /** True for paths under the ephemeral session recorder directory (not persisted slot files). */
  static isSessionCapturePath(path: string | null | undefined): boolean {
    if (!path?.trim()) return false;
    const normalized = path.replace(/\\/g, '/');
    const subdir = VOICE_CLONE_SESSION_SUBDIR;
    return (
      normalized.includes(`/${subdir}/`) ||
      normalized.includes(`${subdir}/`) ||
      normalized.toLowerCase().startsWith(`${subdir}/`)
    );
  }

  hasRecordingData(): boolean {
    return this.recordingData !== null;
  }

  updateRecordingData(updates: Partial<VoiceRecordingData>): void {
    if (!this.recordingData) {
      return;
    }

    this.recordingData = {
      ...this.recordingData,
      ...updates,
    };
  }

  /**
   * Read a file from `Directory.Data` and return the audio data as pure base64. Handles both the
   * `Blob` content stored by `capacitor-blob-writer` and plain base64 strings.
   */
  static async readFileAsBase64(path: string): Promise<string> {
    const { data } = await Filesystem.readFile({
      directory: Directory.Data,
      path,
    });
    if (data instanceof Blob) {
      return filesystemBlobToBase64(data);
    }
    const stringData = typeof data === 'string' ? data : String(data ?? '');
    if (!stringData.length) {
      throw new Error(`Empty file at ${path}`);
    }
    return stringData;
  }

  /** True if we can play back the recording from memory or filesystem. */
  static isPlayable(data: VoiceRecordingData | null | undefined): boolean {
    if (!data?.mimeType) return false;
    if (data.recordFilesystemPath?.length) return true;
    return !!data.recordDataBase64?.length;
  }

  /**
   * True when `data` was loaded from disk for Edit (My Voices) — path under `voice-clone/slots/`.
   */
  static isLoadedPersistedSlotRecording(
    data: VoiceRecordingData | null | undefined
  ): boolean {
    const path = data?.recordFilesystemPath?.trim();
    if (!path) return false;
    const normalized = path.replace(/\\/g, '/').toLowerCase();
    const dir = SLOTS_DIR.toLowerCase();
    return (
      normalized.includes(`${dir}/`) ||
      normalized.includes(`/${dir}/`) ||
      normalized.startsWith(`${dir}/`)
    );
  }

  /**
   * True when the user has a new capture in memory, not the My Voices persisted slot copy.
   */
  static hasInProgressSessionRecording(
    data: VoiceRecordingData | null | undefined
  ): boolean {
    if (!data || !VoiceRecordingService.isPlayable(data)) return false;
    if (data.loadedFromPersistedSlot === true) {
      return false;
    }
    return !VoiceRecordingService.isLoadedPersistedSlotRecording(data);
  }

  /** Filesystem basename under `voice-clone/slots/` for persisted audio + meta. */
  static persistKeyForSlot(
    voiceType: 'mom' | 'dad' | 'custom',
    customNarratorId?: string | null
  ): string {
    if (voiceType === 'mom') return 'mom';
    if (voiceType === 'dad') return 'dad';
    return customNarratorId?.trim() || 'custom';
  }

  /**
   * Load last saved slot recording from disk (after a successful Save on modify).
   * Used when editing an existing Mom/Dad/custom slot from My Voices.
   */
  async loadPersistedSlotRecording(
    voiceType: 'mom' | 'dad' | 'custom',
    meta: { voiceName: string; avatarUrl?: string; customNarratorId?: string | null }
  ): Promise<boolean> {
    const key = VoiceRecordingService.persistKeyForSlot(
      voiceType,
      meta.customNarratorId
    );
    const metaPath = `${SLOTS_DIR}/${key}.meta.json`;
    try {
      const { data } = await Filesystem.readFile({
        directory: Directory.Data,
        path: metaPath,
        encoding: Encoding.UTF8,
      });
      const text = typeof data === 'string' ? data : '';
      const parsed = JSON.parse(text) as PersistedSlotMeta;
      if (!parsed?.audioRelPath || !parsed?.mimeType) return false;

      let recordDataBase64: string | undefined;
      if (!Capacitor.isNativePlatform()) {
        recordDataBase64 = await VoiceRecordingService.readFileAsBase64(
          parsed.audioRelPath
        );
      } else {
        await Filesystem.stat({
          directory: Directory.Data,
          path: parsed.audioRelPath,
        });
      }

      this.recordingData = {
        recordFilesystemPath: parsed.audioRelPath,
        recordDataBase64,
        msDuration: parsed.msDuration,
        mimeType: parsed.mimeType,
        voiceName: meta.voiceName,
        voiceType,
        avatarUrl: meta.avatarUrl,
        customNarratorId:
          voiceType === 'custom' && meta.customNarratorId
            ? meta.customNarratorId
            : undefined,
        loadedFromPersistedSlot: true,
      };
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Writes the saved voice audio for this slot under `voice-clone/slots/` (and `.meta.json`).
   */
  async persistCurrentRecordingForSlot(
    voiceType: 'custom' | 'mom' | 'dad',
    customNarratorId?: string | null
  ): Promise<void> {
    const data = this.recordingData;
    if (!data?.mimeType) return;

    const key = VoiceRecordingService.persistKeyForSlot(
      voiceType,
      customNarratorId ?? data.customNarratorId
    );
    const extension = fileExtensionForMimeType(data.mimeType);
    const audioRelPath = `${SLOTS_DIR}/${key}${extension}`;

    if (data.recordDataBase64) {
      await Filesystem.writeFile({
        directory: Directory.Data,
        path: audioRelPath,
        data: data.recordDataBase64,
        recursive: true,
      });
    } else if (data.recordFilesystemPath) {
      const { data: fileData } = await Filesystem.readFile({
        directory: Directory.Data,
        path: data.recordFilesystemPath,
      });
      const payload = await filesystemReadResultToBase64(fileData);
      await Filesystem.writeFile({
        directory: Directory.Data,
        path: audioRelPath,
        data: payload,
        recursive: true,
      });
    } else {
      return;
    }

    const meta: PersistedSlotMeta = {
      msDuration: data.msDuration,
      mimeType: data.mimeType,
      audioRelPath,
    };
    await Filesystem.writeFile({
      directory: Directory.Data,
      path: `${SLOTS_DIR}/${key}.meta.json`,
      data: JSON.stringify(meta),
      encoding: Encoding.UTF8,
      recursive: true,
    });

    const sourcePath = data.recordFilesystemPath?.trim();
    if (sourcePath && VoiceRecordingService.isSessionCapturePath(sourcePath)) {
      await Filesystem.deleteFile({
        directory: Directory.Data,
        path: sourcePath,
      }).catch(() => undefined);
    }
  }

  /**
   * Writes the saved voice audio for a public request under `voice-clone/requests/<requestId>/`.
   */
  async persistCurrentRecordingForRequest(
    voiceCloneRequestId: string
  ): Promise<void> {
    const data = this.recordingData;
    const requestId = voiceCloneRequestId.trim();
    if (!data?.mimeType || !requestId) return;

    const extension = fileExtensionForMimeType(data.mimeType);
    const requestDir = `${REQUESTS_DIR}/${requestId}`;
    const audioRelPath = `${requestDir}/recording${extension}`;

    if (data.recordDataBase64) {
      await Filesystem.writeFile({
        directory: Directory.Data,
        path: audioRelPath,
        data: data.recordDataBase64,
        recursive: true,
      });
    } else if (data.recordFilesystemPath) {
      const { data: fileData } = await Filesystem.readFile({
        directory: Directory.Data,
        path: data.recordFilesystemPath,
      });
      const payload = await filesystemReadResultToBase64(fileData);
      await Filesystem.writeFile({
        directory: Directory.Data,
        path: audioRelPath,
        data: payload,
        recursive: true,
      });
    } else {
      return;
    }

    const meta: PersistedSlotMeta = {
      msDuration: data.msDuration,
      mimeType: data.mimeType,
      audioRelPath,
    };
    await Filesystem.writeFile({
      directory: Directory.Data,
      path: `${requestDir}/recording.meta.json`,
      data: JSON.stringify(meta),
      encoding: Encoding.UTF8,
      recursive: true,
    });

    const sourcePath = data.recordFilesystemPath?.trim();
    if (sourcePath && VoiceRecordingService.isSessionCapturePath(sourcePath)) {
      await Filesystem.deleteFile({
        directory: Directory.Data,
        path: sourcePath,
      }).catch(() => undefined);
    }
  }

  /**
   * Removes persisted audio + meta for a voice clone request under
   * `voice-clone/requests/<requestId>/` (e.g. after cancel or local dismiss).
   */
  async deletePersistedRequestArtifacts(
    voiceCloneRequestId: string
  ): Promise<void> {
    const requestId = voiceCloneRequestId.trim();
    if (!requestId) {
      return;
    }
    await Filesystem.rmdir({
      directory: Directory.Data,
      path: `${REQUESTS_DIR}/${requestId}`,
      recursive: true,
    }).catch(() => undefined);
  }

  /**
   * Removes persisted slot audio + meta from app Data.
   */
  async deletePersistedSlotRecording(
    voiceType: 'mom' | 'dad' | 'custom',
    customNarratorId?: string | null
  ): Promise<void> {
    const key = VoiceRecordingService.persistKeyForSlot(
      voiceType,
      customNarratorId
    );
    const metaPath = `${SLOTS_DIR}/${key}.meta.json`;
    try {
      const { data } = await Filesystem.readFile({
        directory: Directory.Data,
        path: metaPath,
        encoding: Encoding.UTF8,
      });
      const text = typeof data === 'string' ? data : '';
      const parsed = JSON.parse(text) as PersistedSlotMeta;
      if (parsed?.audioRelPath) {
        await Filesystem.deleteFile({
          directory: Directory.Data,
          path: parsed.audioRelPath,
        }).catch(() => undefined);
      }
    } catch {
      /* ignore missing meta */
    }

    await Filesystem.deleteFile({
      directory: Directory.Data,
      path: metaPath,
    }).catch(() => undefined);

    for (const ext of ['.aac', '.m4a', '.webm', '.mp3', '.ogg', '.wav']) {
      await Filesystem.deleteFile({
        directory: Directory.Data,
        path: `${SLOTS_DIR}/${key}${ext}`,
      }).catch(() => undefined);
    }
  }

  /**
   * Persist a web capture under Directory.Data while the UI still uses inline base64 for playback.
   */
  async persistWebSessionRecording(
    base64: string,
    mimeType: string
  ): Promise<string> {
    const ext = fileExtensionForMimeType(mimeType);
    const path = `${VOICE_CLONE_SESSION_SUBDIR}/recording-${Date.now()}${ext}`;
    await Filesystem.writeFile({
      directory: Directory.Data,
      path,
      data: base64.replace(/\s/g, ''),
      recursive: true,
    });
    return path;
  }

  /** Base64 for share/upload when recording may only exist as a Data-directory file. */
  async getRecordingAsBase64(): Promise<{ base64: string; mimeType: string } | null> {
    const data = this.recordingData;
    if (!data?.mimeType) return null;
    if (data.recordDataBase64?.length) {
      return { base64: data.recordDataBase64, mimeType: data.mimeType };
    }
    if (data.recordFilesystemPath) {
      const { data: fileData } = await Filesystem.readFile({
        directory: Directory.Data,
        path: data.recordFilesystemPath,
      });
      const base64 = await filesystemReadResultToBase64(fileData);
      return { base64, mimeType: data.mimeType };
    }
    return null;
  }
}

function fileExtensionForMimeType(mimeType: string): string {
  if (mimeType.includes('webm')) return '.webm';
  if (mimeType.includes('ogg')) return '.ogg';
  if (mimeType.includes('mpeg') || mimeType.includes('mp3')) return '.mp3';
  if (mimeType.includes('wav')) return '.wav';
  if (mimeType.includes('mp4')) return '.m4a';
  if (mimeType.includes('aac')) return '.aac';
  return '.m4a';
}

/** Convert a Blob to a pure base64 string (no data-URL prefix). */
async function filesystemBlobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const value = String(reader.result);
      const base64Index = value.indexOf('base64,');
      resolve(base64Index >= 0 ? value.slice(base64Index + 7).trim() : value.trim());
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function filesystemReadResultToBase64(data: string | Blob): Promise<string> {
  if (typeof data === 'string') return data;
  return filesystemBlobToBase64(data);
}
